import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Bug, Droplets, Calculator, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { sendOrQueue, queuedToast } from '../utils/offlineStore';

const API = process.env.REACT_APP_BACKEND_URL;

const METHODS = [
  { id: 'alcohol_wash', name: 'Alcohol Wash', description: '~300 bees in alcohol, count mites' },
  { id: 'sugar_roll', name: 'Sugar Roll', description: 'Powdered sugar method, non-lethal' },
  { id: 'sticky_board', name: 'Sticky Board', description: '24-72 hour natural drop count' },
  { id: 'visual', name: 'Visual Inspection', description: 'Count mites on bees/brood' }
];

const MiteCountDialog = ({ open, onClose, token, hives = [], onSuccess, preselectedHiveId = null }) => {
  const [formData, setFormData] = useState({
    hive_id: '',
    date: new Date().toISOString().split('T')[0],
    method: 'alcohol_wash',
    mite_count: '',
    bee_count: '300',
    mite_type: 'varroa',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Auto-select hive when preselectedHiveId is provided
  useEffect(() => {
    if (open && preselectedHiveId) {
      setFormData(prev => ({ ...prev, hive_id: preselectedHiveId }));
    }
  }, [open, preselectedHiveId]);

  const selectedHive = hives.find(h => h.id === formData.hive_id);

  const calculatePercentage = () => {
    const mites = parseFloat(formData.mite_count) || 0;
    const bees = parseFloat(formData.bee_count) || 300;
    return bees > 0 ? ((mites / bees) * 100).toFixed(2) : 0;
  };

  const percentage = calculatePercentage();
  const isHighRisk = parseFloat(percentage) >= 3;

  const handleSubmit = async () => {
    if (!formData.hive_id) {
      toast.error('Please select a hive');
      return;
    }
    if (!formData.mite_count) {
      toast.error('Please enter mite count');
      return;
    }

    setSaving(true);
    try {
      const { queued } = await sendOrQueue({ path: '/mite-counts', kind: 'mite_count', label: `Mite count · Hive ${selectedHive?.hive_number || ''}`, hiveIds: [formData.hive_id], body: {
        ...formData,
        hive_number: selectedHive?.hive_number || '',
        mite_count: parseInt(formData.mite_count),
        bee_count: parseInt(formData.bee_count)
      } });
      if (queued) queuedToast('mite count'); else toast.success('Mite count recorded!');
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error('Failed to save mite count');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      hive_id: '',
      date: new Date().toISOString().split('T')[0],
      method: 'alcohol_wash',
      mite_count: '',
      bee_count: '300',
      mite_type: 'varroa',
      notes: ''
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-red-600" />
            Record Mite Count
          </DialogTitle>
          <DialogDescription>
            Track mite levels to determine when treatment is needed
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Hive Selection - Show dropdown only if no preselected hive */}
          {preselectedHiveId && selectedHive ? (
            <div>
              <Label>Hive</Label>
              <div className="mt-1 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <span className="font-medium text-amber-800">
                  #{selectedHive.hive_number} {selectedHive.nickname ? `"${selectedHive.nickname}"` : ''} - {selectedHive.yard || 'Unknown Yard'}
                </span>
              </div>
            </div>
          ) : (
            <div>
              <Label>Hive *</Label>
              <Select 
                value={formData.hive_id} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, hive_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select hive..." />
                </SelectTrigger>
                <SelectContent>
                  {hives.map(hive => (
                    <SelectItem key={hive.id} value={hive.id}>
                      #{hive.hive_number} - {hive.yard || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date and Mite Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Mite Type</Label>
              <Select 
                value={formData.mite_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, mite_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="varroa">Varroa</SelectItem>
                  <SelectItem value="tropilaelaps">Tropilaelaps</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Method */}
          <div>
            <Label>Testing Method</Label>
            <Select 
              value={formData.method} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, method: v }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHODS.map(method => (
                  <SelectItem key={method.id} value={method.id}>
                    {method.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {METHODS.find(m => m.id === formData.method)?.description}
            </p>
          </div>

          {/* Counts */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Mite Count *</Label>
              <Input
                type="number"
                min="0"
                value={formData.mite_count}
                onChange={(e) => setFormData(prev => ({ ...prev, mite_count: e.target.value }))}
                placeholder="e.g., 9"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Bee Sample Size</Label>
              <Input
                type="number"
                min="1"
                value={formData.bee_count}
                onChange={(e) => setFormData(prev => ({ ...prev, bee_count: e.target.value }))}
                placeholder="300"
                className="mt-1"
              />
            </div>
          </div>

          {/* Calculated Percentage */}
          {formData.mite_count && (
            <div className={`p-4 rounded-lg border-2 ${
              isHighRisk 
                ? 'bg-red-50 border-red-300' 
                : 'bg-green-50 border-green-300'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-gray-600" />
                  <span className="font-medium">Infestation Rate</span>
                </div>
                <span className={`text-2xl font-bold ${isHighRisk ? 'text-red-600' : 'text-green-600'}`}>
                  {percentage}%
                </span>
              </div>
              <p className={`text-sm mt-2 ${isHighRisk ? 'text-red-700' : 'text-green-700'}`}>
                {isHighRisk 
                  ? '⚠️ Above 3% threshold - Treatment recommended!' 
                  : '✓ Below treatment threshold - Continue monitoring'}
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any observations during testing..."
              className="mt-1"
              rows={2}
            />
          </div>

          {/* Info */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
            <div className="flex gap-2">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-blue-800">
                <p className="font-medium">Treatment Thresholds:</p>
                <p className="text-blue-700 mt-1">
                  • &lt;2%: Safe, monitor monthly<br/>
                  • 2-3%: Concerning, plan treatment<br/>
                  • &gt;3%: Critical, treat immediately
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={saving}
              className="flex-1 bg-amber-500 hover:bg-amber-600"
            >
              {saving ? 'Saving...' : 'Save Count'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MiteCountDialog;
